/**
 * Deterministic JSON-like string for cache keys: object keys sorted; arrays keep order.
 * Handles primitives, Date, bigint, circular references (placeholder), and max depth.
 */
const MAX_DEPTH = 48

export function stableStringify(value: unknown): string {
  return stringifyInner(value, new WeakSet<object>(), 0)
}

function stringifyInner(value: unknown, seen: WeakSet<object>, depth: number): string {
  if (depth > MAX_DEPTH) return '"[MaxDepth]"'
  if (value === null) return "null"
  if (value === undefined) return "null"

  const t = typeof value
  if (t === "string") return JSON.stringify(value)
  if (t === "number" || t === "boolean") return JSON.stringify(value)
  if (t === "bigint") return JSON.stringify(String(value))
  if (value instanceof Date) return JSON.stringify(value.toISOString())

  if (Array.isArray(value)) {
    return `[${value.map((v) => stringifyInner(v, seen, depth + 1)).join(",")}]`
  }

  if (t === "object") {
    const obj = value as Record<string, unknown>
    if (seen.has(obj)) return '"[Circular]"'
    seen.add(obj)
    const keys = Object.keys(obj).sort()
    const parts = keys.map((k) => `${JSON.stringify(k)}:${stringifyInner(obj[k], seen, depth + 1)}`)
    return `{${parts.join(",")}}`
  }

  return JSON.stringify(String(value))
}
